<template>
  <v-card
    class="dashboard-card-shell"
    :class="{ 'dashboard-card-has-info': cardMetadata && cardMetadata.description, 'dashboard-card-has-export': exportSource }"
    elevation="2"
  >
    <v-dialog v-model="showInformation" max-width="720" scrollable>
      <v-card v-if="cardMetadata">
        <v-card-title class="align-start flex-nowrap" style="word-break: normal;">
          <span>{{ cardMetadata.name }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            class="ml-2"
            :aria-label="$dashboardText('close')"
            @click="showInformation = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-body-1 dashboard-card-description">
            <div
              v-for="(block, index) in descriptionBlocks"
              :key="index"
              :class="block.isFormula ? 'dashboard-card-formula' : 'dashboard-card-paragraph'"
            >
              <div v-if="block.isFormula" class="text-caption font-weight-medium mb-2">{{ $dashboardText('calculation') }}</div>
              <div :class="{ 'dashboard-card-formula-text': block.isFormula }">{{ block.text }}</div>
            </div>
          </div>
          <div v-if="isSupport" class="mt-4 text-caption">
            <v-divider class="mb-3"></v-divider>
            <div class="font-weight-medium mb-1">Utvecklarinformation</div>
            <div class="dashboard-card-developer-information-row">
              <span>Komponent: <code>{{ cardMetadata.component_name }}</code></span>
              <copy-to-clipboard-button :text="cardMetadata.component_name"></copy-to-clipboard-button>
            </div>
            <div class="text--secondary dashboard-card-method dashboard-card-developer-information-row">
              <span>Controller: {{ cardMetadata.controller_method }}</span>
              <copy-to-clipboard-button :text="cardMetadata.controller_method"></copy-to-clipboard-button>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <template v-if="componentName === 'weather-forecast'">
      <weather-forecast></weather-forecast>
    </template>
    <template v-else-if="componentName === 'bathing-water-temperatures'">
      <bathing-water-temperatures :dashboard-card-settings="dashboardCardSettings" :pivot-id="pivotId" @updated-dashboard-card-settings="updatedDashboardCardSettings"></bathing-water-temperatures>
    </template>
    <template v-else-if="componentName === 'product-group-sales-table'">
      <product-group-sales-table @dashboard-export-ready="setExportSource"></product-group-sales-table>
    </template>
    <template v-else-if="componentName === 'bookings-per-channel'">
      <bookings-per-channel @dashboard-export-ready="setExportSource"></bookings-per-channel>
    </template>
    <template v-else-if="componentName === 'checkins-by-user'">
      <checkins-by-user @dashboard-export-ready="setExportSource"></checkins-by-user>
    </template>
    <template v-else-if="componentName === 'checkouts-by-user'">
      <checkouts-by-user @dashboard-export-ready="setExportSource"></checkouts-by-user>
    </template>
    <template v-else-if="componentName === 'checkins-and-checkouts-per-hour'">
      <checkins-and-checkouts-per-hour @dashboard-export-ready="setExportSource"></checkins-and-checkouts-per-hour>
    </template>
    <template v-else-if="componentName === 'checkinouts-calendar'">
      <checkinouts-calendar @dashboard-export-ready="setExportSource"></checkinouts-calendar>
    </template>
    <template v-else-if="componentName === 'checked-in-customers'">
      <checked-in-customers
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></checked-in-customers>
    </template>
    <template v-else-if="componentName === 'active-customers'">
      <active-customers @dashboard-export-ready="setExportSource"></active-customers>
    </template>
    <template v-else-if="componentName === 'customer-nationalities'">
      <customer-nationalities
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></customer-nationalities>
    </template>
    <template v-else-if="componentName === 'revenue-numbers'">
      <revenue-numbers @dashboard-export-ready="setExportSource"></revenue-numbers>
    </template>
    <template v-else-if="componentName === 'category-occupation-list'">
      <category-occupation-list
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></category-occupation-list>
    </template>
    <template v-else-if="componentName === 'category-occupation-radar'">
      <category-occupation-radar
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></category-occupation-radar>
    </template>
    <template v-else-if="componentName === 'total-occupation'">
      <total-occupation
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></total-occupation>
    </template>
    <template v-else-if="componentName === 'accommodation-value'">
      <accommodation-value @dashboard-export-ready="setExportSource"></accommodation-value>
    </template>
    <template v-else-if="componentName === 'average-booking-value'">
      <average-booking-value
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></average-booking-value>
    </template>
    <template v-else-if="componentName === 'historical-turnover-full-accrual'">
      <historical-turnover-full-accrual @dashboard-export-ready="setExportSource"></historical-turnover-full-accrual>
    </template>
    <template v-else-if="componentName === 'historical-turnover-part-accrual'">
      <historical-turnover-part-accrual @dashboard-export-ready="setExportSource"></historical-turnover-part-accrual>
    </template>
    <template v-else-if="componentName === 'occupancy-stats'">
      <occupancy-stats
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></occupancy-stats>
    </template>
    <template v-else-if="componentName === 'booking-stats'">
      <booking-stats
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></booking-stats>
    </template>
    <template v-else-if="componentName === 'position-turnover'">
      <position-turnover @dashboard-export-ready="setExportSource"></position-turnover>
    </template>
    <template v-else-if="componentName === 'category-turnover'">
      <category-turnover @dashboard-export-ready="setExportSource"></category-turnover>
    </template>
    <template v-else-if="componentName === 'position-occupation'">
      <position-occupation
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></position-occupation>
    </template>
    <template v-else-if="componentName === 'category-occupation'">
      <category-occupation
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></category-occupation>
    </template>
    <template v-else-if="componentName === 'stay-durations'">
      <stay-durations
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></stay-durations>
    </template>
    <template v-else-if="componentName === 'fully-booked-days'">
      <fully-booked-days
        @dashboard-export-ready="setExportSource"
      ></fully-booked-days>
    </template>
    <template v-else-if="componentName === 'yearly-guest-nights-summary'">
      <yearly-guest-nights-summary
        :dashboard-card-settings="dashboardCardSettings"
        :pivot-id="pivotId"
        @updated-dashboard-card-settings="updatedDashboardCardSettings"
        @dashboard-export-ready="setExportSource"
      ></yearly-guest-nights-summary>
    </template>
    <template v-else-if="componentName === 'access-control'">
      <access-control></access-control>
    </template>
    <template v-else>
      <v-alert type="error">{{ $dashboardText('unknownCard', { name: componentName }) }}</v-alert>
    </template>

    <v-menu v-if="exportSource" offset-y left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          small
          class="dashboard-card-export-button"
          :loading="isExporting"
          :disabled="!exportReady || isExporting"
          :aria-label="$dashboardText('exportCard', { name: cardMetadata ? cardMetadata.name : $dashboardText('card') })"
          v-bind="attrs"
          v-on="on"
          @click.stop
        >
          <v-icon small>mdi-download</v-icon>
        </v-btn>
      </template>
      <v-list dense>
        <v-list-item :disabled="!exportReady || isExporting" @click="exportCard('xlsx')">
          <v-list-item-icon><v-icon small>mdi-microsoft-excel</v-icon></v-list-item-icon>
          <v-list-item-title>{{ $dashboardText('exportExcel') }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="supportsChartExport" :disabled="!exportReady || isExporting" @click="exportCard('png')">
          <v-list-item-icon><v-icon small>mdi-image-outline</v-icon></v-list-item-icon>
          <v-list-item-title>{{ $dashboardText('saveChart') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-btn
      v-if="cardMetadata && cardMetadata.description"
      icon
      small
      class="dashboard-card-info-button"
      :aria-label="$dashboardText('cardInformation', { name: cardMetadata.name })"
      @click.stop="showInformation = true"
    >
      <v-icon small>mdi-information-outline</v-icon>
    </v-btn>
  </v-card>
</template>

<style scoped>
.dashboard-card-shell {
  --dashboard-card-controls-space: 16px;
  position: relative;
  min-width: 0;
}
.dashboard-card-has-info {
  --dashboard-card-controls-space: 48px;
}
.dashboard-card-shell:has(.dashboard-card-settings-button) {
  --dashboard-card-controls-space: 56px;
}
.dashboard-card-has-info:has(.dashboard-card-settings-button) {
  --dashboard-card-controls-space: 88px;
}
.dashboard-card-shell ::v-deep > .v-card > .v-card__title {
  padding-right: var(--dashboard-card-controls-space);
  word-break: normal;
  overflow-wrap: anywhere;
  gap: 8px;
}
.dashboard-card-shell ::v-deep .dashboard-card-settings-button {
  top: 8px !important;
  right: 8px !important;
}
.dashboard-card-shell ::v-deep .dashboard-card-summary {
  position: relative;
  padding-right: calc(var(--dashboard-card-controls-space) - 16px);
  flex-wrap: wrap;
  gap: 12px 16px;
}
.dashboard-card-shell ::v-deep .dashboard-card-summary:has(> .v-icon) {
  padding-right: max(calc(var(--dashboard-card-controls-space) - 16px), 76px);
}
.dashboard-card-shell ::v-deep .dashboard-card-summary > .v-icon {
  position: absolute;
  right: 0;
  bottom: 0;
}
.dashboard-card-shell ::v-deep .dashboard-card-summary > div > .d-flex {
  flex-wrap: wrap;
}
.dashboard-card-shell ::v-deep .dashboard-card-summary-content:has(.v-alert) {
  padding-right: var(--dashboard-card-controls-space);
}
.dashboard-card-shell > .v-card {
  box-shadow: none !important;
}
.dashboard-card-info-button {
  position: absolute;
  top: 12px;
  right: 8px;
  z-index: 3;
}
.dashboard-card-shell:has(.dashboard-card-settings-button) > .dashboard-card-info-button {
  right: 48px;
}
.dashboard-card-has-export {
  --dashboard-card-controls-space: 48px;
}
.dashboard-card-has-export.dashboard-card-has-info {
  --dashboard-card-controls-space: 88px;
}
.dashboard-card-has-export:has(.dashboard-card-settings-button) {
  --dashboard-card-controls-space: 96px;
}
.dashboard-card-has-export.dashboard-card-has-info:has(.dashboard-card-settings-button) {
  --dashboard-card-controls-space: 128px;
}
.dashboard-card-export-button {
  position: absolute;
  top: 12px;
  right: 8px;
  z-index: 3;
}
.dashboard-card-shell:has(.dashboard-card-settings-button) .dashboard-card-export-button {
  right: 48px;
}
.dashboard-card-has-export > .dashboard-card-info-button {
  right: 48px;
}
.dashboard-card-has-export:has(.dashboard-card-settings-button) > .dashboard-card-info-button {
  right: 88px;
}
.dashboard-card-method {
  overflow-wrap: anywhere;
}
.dashboard-card-developer-information-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.dashboard-card-description {
  line-height: 1.6;
}
.dashboard-card-description > div + div {
  margin-top: 16px;
}
.dashboard-card-paragraph,
.dashboard-card-formula-text {
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.dashboard-card-formula {
  padding: 12px 16px;
  border-left: 3px solid currentColor;
  border-radius: 4px;
  background-color: rgba(127, 127, 127, 0.08);
}
.dashboard-card-formula-text {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.9375rem;
  line-height: 1.8;
}
</style>

<script>
import { mapGetters, mapState } from 'vuex'
import CopyToClipboardButton from '@/components/common/CopyToClipboardButton.vue'
import { cloneDashboardExport, downloadDashboardWorkbook, downloadDashboardChart } from '@/utils/dashboardExport'

export default {
  name: 'DashboardCard',
  components: {
    CopyToClipboardButton,
    /* Endast komponenten som behövs laddas in */
    WeatherForecast: () => import('./WeatherForecast'),
    BathingWaterTemperatures: () => import('@/components/statistics/BathingWaterTemperatures'),
    ProductGroupSalesTable: () => import('@/components/statistics/ProductGroupSalesTable'),
    BookingsPerChannel: () => import('@/components/statistics/BookingsPerChannel'),
    CheckinsByUser: () => import('@/components/statistics/CheckinsByUser'),
    CheckoutsByUser: () => import('@/components/statistics/CheckoutsByUser'),
    CheckinsAndCheckoutsPerHour: () => import('@/components/statistics/CheckinsAndCheckoutsPerHour'),
    CheckinoutsCalendar: () => import('./CheckinoutsCalendar'),
    CheckedInCustomers: () => import('@/components/statistics/CheckedInCustomers'),
    ActiveCustomers: () => import('@/components/statistics/ActiveCustomers'),
    CustomerNationalities: () => import('@/components/statistics/CustomerNationalities'),
    RevenueNumbers: () => import('@/components/statistics/RevenueNumbers'),
    CategoryOccupationList: () => import('@/components/statistics/CategoryOccupationList'),
    CategoryOccupationRadar: () => import('@/components/statistics/CategoryOccupationRadar'),
    TotalOccupation: () => import('@/components/statistics/TotalOccupation'),
    AccommodationValue: () => import('@/components/statistics/AccommodationValue'),
    AverageBookingValue: () => import('@/components/statistics/AverageBookingValue'),
    HistoricalTurnoverFullAccrual: () => import('@/components/statistics/HistoricalTurnoverFullAccrual'),
    HistoricalTurnoverPartAccrual: () => import('@/components/statistics/HistoricalTurnoverPartAccrual'),
    OccupancyStats: () => import('@/components/statistics/OccupancyStats'),
    BookingStats: () => import('@/components/statistics/BookingStats'),
    PositionTurnover: () => import('@/components/statistics/PositionTurnover'),
    CategoryTurnover: () => import('@/components/statistics/CategoryTurnover'),
    PositionOccupation: () => import('@/components/statistics/PositionOccupation'),
    CategoryOccupation: () => import('@/components/statistics/CategoryOccupation'),
    StayDurations: () => import('@/components/statistics/StayDurations'),
    FullyBookedDays: () => import('@/components/statistics/FullyBookedDays'),
    YearlyGuestNightsSummary: () => import('@/components/statistics/YearlyGuestNightsSummary'),
    AccessControl: () => import('./AccessControl')
  },
  props: {
    componentName: String,
    card: Object,
    dashboardCardSettings: Object,
    pivotId: Number
  },
  data: () => ({
    showInformation: false,
    exportSource: null,
    isExporting: false
  }),
  computed: {
    ...mapGetters({ activeAuthSession: 'user/activeAuthSession' }),
    ...mapState({ dashboardCards: state => state.stats.dashboardCards }),
    exportReady () {
      return !!this.exportSource?.canExportDashboard
    },
    supportsChartExport () {
      return typeof this.exportSource?.getDashboardChart === 'function'
    },
    cardMetadata: function () {
      return this.card || this.dashboardCards.find(card => card.component_name === this.componentName) || null
    },
    descriptionBlocks: function () {
      const description = this.cardMetadata?.description || ''
      return description.replace(/\r\n/g, '\n').split(/\n\s*\n/).filter(block => block.trim()).map(block => {
        const formula = block.trim().match(/^```formula\n([\s\S]+?)\n```$/)
        return { isFormula: !!formula, text: formula ? formula[1] : block }
      })
    },
    isSupport: function () {
      return this.activeAuthSession?.user?.is_support ?? false
    }
  },
  methods: {
    setExportSource (source) {
      this.exportSource = source
    },
    async exportCard (format) {
      if (!this.exportReady || this.isExporting) { return }
      this.isExporting = true
      const source = this.exportSource
      try {
        // Let Vue and vue-chartjs apply the most recent control change first.
        await this.$nextTick()
        if (source !== this.exportSource || !source.canExportDashboard) { return }
        const snapshot = cloneDashboardExport(source.getDashboardExport())
        snapshot.title = snapshot.title || this.cardMetadata?.name || 'Dashboard'
        snapshot.exportedAt = new Date().toISOString()
        snapshot.exportLabels = {
          report: this.$dashboardText('report'),
          exported: this.$dashboardText('exported'),
          parameter: this.$dashboardText('parameter'),
          parameters: this.$dashboardText('parameters'),
          value: this.$dashboardText('value'),
          source: this.$dashboardText('source')
        }
        if (format === 'png') {
          await downloadDashboardChart(source.getDashboardChart(), snapshot)
        } else {
          await downloadDashboardWorkbook(snapshot)
        }
      } catch (error) {
        console.error('Dashboard export failed', error)
        this.$store.dispatch('snackbars/createSnackbar', {
          text: this.$dashboardText('exportFailed'),
          color: 'error'
        })
      } finally {
        this.isExporting = false
      }
    },
    updatedDashboardCardSettings: function (param) {
      this.$emit('updated-dashboard-card-settings', param)
    }
  },
  mounted () {}
}
</script>
